import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';

import { SnSearchItem } from '@lib/api/entities';
import { SnSearchAPI } from '@lib/api/services';
import { t, TranslationKey } from '@lib/translation';
import { SnSearchbarOptionConfig } from '@lib/ui/searchbar';

import { SearchItemClickEvent, MapSearchServiceInterface } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class MapSearchService implements MapSearchServiceInterface, OnDestroy {
  private readonly _searchItemClick$ = new Subject<SearchItemClickEvent>();

  public get searchItemClick$(): Observable<SearchItemClickEvent> {
    return this._searchItemClick$.asObservable();
  }

  constructor(private readonly _searchAPI: SnSearchAPI) {}

  public ngOnDestroy(): void {
    this._searchItemClick$.complete();
  }

  public search(value: string): Observable<SnSearchbarOptionConfig[]> {
    if (!value) {
      return of([]);
    }

    return this._searchAPI.search(value).pipe(
      map(({ fusus, googlePlaces, googleAddresses, avlResults, cadResults, assets }) => {
        const fususList = fusus ?? [];
        const avlList = avlResults ?? [];
        const cadList = cadResults ?? [];
        const placesList = googlePlaces ?? [];
        const addressesList = googleAddresses ?? [];
        const assetsList = assets ?? [];

        const defs: Array<{
          labelKey: TranslationKey;
          items: SnSearchItem[];
          clickType: 'search' | 'address';
          filterGeometry?: boolean;
        }> = [
          { labelKey: 'common.assets', items: assetsList, clickType: 'search' },
          { labelKey: 'common.fusus', items: fususList, clickType: 'search', filterGeometry: true },
          { labelKey: 'common.cad', items: cadList, clickType: 'search', filterGeometry: true },
          { labelKey: 'common.avl', items: avlList, clickType: 'search', filterGeometry: true },
          { labelKey: 'map.appbar.searchResults.places', items: placesList, clickType: 'search' },
          {
            labelKey: 'map.appbar.searchResults.addresses',
            items: addressesList,
            clickType: 'address',
          },
        ];

        return defs.flatMap((d) => {
          const sourceItems = d.filterGeometry ? d.items.filter((it) => !!it.geometry) : d.items;
          if (!sourceItems || sourceItems.length === 0) return [];

          const options = this._convertToOptions(sourceItems, d.clickType);
          return this._convertToOptionGroup(t(d.labelKey), options);
        });
      }),
      catchError(() => of([])),
    );
  }

  public emitSearchItemClick(item: SnSearchItem, type: 'search' | 'address'): void {
    this._searchItemClick$.next({ item, type });
  }

  private _convertToOptionGroup(
    label: string,
    options: SnSearchbarOptionConfig[],
  ): SnSearchbarOptionConfig[] {
    if (!options.length) {
      return [];
    }

    return [
      {
        isLabel: true,
        label,
      },
      ...options,
    ];
  }

  private _convertToOptions(
    items: SnSearchItem[],
    clickType: 'search' | 'address',
  ): SnSearchbarOptionConfig[] {
    return items.map((item) => {
      const { id, geometry, icon, coords, ...rest } = item;
      return {
        ...rest,
        icon,
        value: item.label,
        click: () => this.emitSearchItemClick(item, clickType),
      };
    });
  }
}
